import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  User, 
  Mail, 
  Phone, 
  CreditCard, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Plus,
  Trash2,
  Save,
  X
} from 'lucide-react';
import { lightTheme } from '../../styles/tokens/colors';

const ReadOnlyField = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
    <p className="text-sm mt-0.5">{value || value === 0 ? value : <span className="text-muted-foreground">No especificado</span>}</p>
  </div>
);

// Formatea una fecha SIN aplicar zona horaria. Una fecha de nacimiento es un
// día calendario, no un instante: construir `new Date("2000-01-01")` la
// interpreta como UTC medianoche y al mostrarla en zona Chile (UTC-4) retrocede
// al día anterior. Por eso parseamos los componentes YYYY-MM-DD directamente.
const fmtFecha = (val) => {
  if (!val) return '';
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`; // DD-MM-YYYY
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('es-CL');
};

// Normaliza a YYYY-MM-DD para <input type="date"> sin desfase de zona horaria.
const toDateInput = (val) => {
  if (!val) return '';
  const m = String(val).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};

const ContratanteViewer = ({ contratante, cargas, onClose }) => {
  const titular = contratante?.datos_titular || {};
  const nombreTitular = [titular.nombre, titular.apellido_paterno, titular.apellido_materno].filter(Boolean).join(' ');

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>Datos del Contratante</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Datos del titular */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" /> Datos del Titular
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <ReadOnlyField label="Nombre completo" value={nombreTitular || contratante?.nombre} />
              <ReadOnlyField label="RUT" value={titular.rut || contratante?.rut} />
              <ReadOnlyField label="Email" value={titular.email || contratante?.mail} />
              <ReadOnlyField label="Teléfono" value={titular.telefono || contratante?.telefono} />
              <ReadOnlyField label="Dirección" value={titular.direccion} />
              <ReadOnlyField label="Comuna" value={titular.comuna} />
              <ReadOnlyField label="Región" value={titular.region} />
              <ReadOnlyField label="Fecha de nacimiento" value={fmtFecha(titular.fecha_nacimiento)} />
              <div>
                <p className="text-xs font-medium text-muted-foreground">Estado</p>
                <Badge variant="outline" className="mt-0.5">{contratante?.estado || '—'}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Cargas familiares */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" /> Cargas Familiares
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cargas.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <User className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay cargas familiares registradas</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cargas.map((carga, index) => {
                    const nombreCarga = [carga.nombre, carga.apellido_paterno, carga.apellido_materno].filter(Boolean).join(' ');
                    return (
                      <div key={carga.id || index} className="border rounded-lg p-4">
                        <p className="font-medium mb-3">Carga {index + 1}</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <ReadOnlyField label="Nombre completo" value={nombreCarga} />
                          <ReadOnlyField label="RUT" value={carga.rut} />
                          <ReadOnlyField label="Edad" value={carga.edad} />
                          <ReadOnlyField label="Sexo" value={carga.sexo === 'M' ? 'Masculino' : carga.sexo === 'F' ? 'Femenino' : carga.sexo} />
                          <ReadOnlyField label="Previsión" value={carga.prevision} />
                          <ReadOnlyField label="Fecha de nacimiento" value={fmtFecha(carga.fecha_nacimiento)} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end pt-4 border-t mt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ContratanteForm = ({
  isOpen,
  onClose,
  contratante = null,
  onSave,
  readOnly = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cargas, setCargas] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      nombre: '',
      rut: '',
      mail: '',
      telefono: '',
      estado: 'pendiente',
      datos_titular: {
        nombre: '',
        apellido_paterno: '',
        apellido_materno: '',
        rut: '',
        telefono: '',
        email: '',
        direccion: '',
        comuna: '',
        region: '',
        fecha_nacimiento: ''
      }
    }
  });

  const watchedData = watch();

  // Pasos del stepper
  const steps = [
    {
      id: 'datos_titular',
      title: 'Datos del Titular',
      icon: User,
      description: 'Información del titular del seguro'
    },
    {
      id: 'cargas_familiares',
      title: 'Cargas Familiares',
      icon: User,
      description: 'Información de cargas familiares'
    },
    {
      id: 'estado_proceso',
      title: 'Estado del Proceso',
      icon: CheckCircle,
      description: 'Estado de pago y certificación'
    }
  ];

  useEffect(() => {
    if (contratante) {
      // Cargar datos del contratante
      reset({
        nombre: contratante.nombre || '',
        rut: contratante.rut || '',
        mail: contratante.mail || '',
        telefono: contratante.telefono || '',
        estado: contratante.estado || 'pendiente',
        datos_titular: {
          nombre: '',
          apellido_paterno: '',
          apellido_materno: '',
          rut: '',
          telefono: '',
          email: '',
          direccion: '',
          comuna: '',
          region: '',
          ...(contratante.datos_titular || {}),
          // El input date exige YYYY-MM-DD puro (sin hora ni zona).
          fecha_nacimiento: toDateInput(contratante.datos_titular?.fecha_nacimiento)
        }
      });
      
      // Cargar cargas familiares si existen
      if (contratante.contratacion_cargas) {
        setCargas(contratante.contratacion_cargas);
      }
    }
  }, [contratante, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      const formData = {
        ...data,
        contratacion_cargas: cargas
      };
      
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.message || 'Error al guardar el contratante');
    } finally {
      setLoading(false);
    }
  };

  const addCarga = () => {
    const newCarga = {
      id: Date.now(),
      tipo: 'carga',
      nombre: '',
      apellido_paterno: '',
      apellido_materno: '',
      rut: '',
      edad: '',
      sexo: '',
      prevision: '',
      fecha_nacimiento: '',
      datos_carga: {}
    };
    setCargas([...cargas, newCarga]);
  };

  const removeCarga = (index) => {
    setCargas(cargas.filter((_, i) => i !== index));
  };

  const updateCarga = (index, field, value) => {
    const updatedCargas = [...cargas];
    updatedCargas[index] = {
      ...updatedCargas[index],
      [field]: value
    };
    setCargas(updatedCargas);
  };

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'pending';
  };

  const getStepIcon = (step, status) => {
    const IconComponent = step.icon;
    if (status === 'completed') {
      return <CheckCircle className="h-5 w-5" style={{ color: lightTheme.success.main }} />;
    }
    if (status === 'current') {
      return <IconComponent className="h-5 w-5" style={{ color: lightTheme.primary.main }} />;
    }
    return <IconComponent className="h-5 w-5" style={{ color: lightTheme.text.secondary }} />;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="titular_nombre">Nombre</Label>
                <Input
                  id="titular_nombre"
                  {...register('datos_titular.nombre')}
                  placeholder="Nombre"
                />
              </div>
              <div>
                <Label htmlFor="titular_apellido_paterno">Apellido Paterno</Label>
                <Input
                  id="titular_apellido_paterno"
                  {...register('datos_titular.apellido_paterno')}
                  placeholder="Apellido Paterno"
                />
              </div>
              <div>
                <Label htmlFor="titular_apellido_materno">Apellido Materno</Label>
                <Input
                  id="titular_apellido_materno"
                  {...register('datos_titular.apellido_materno')}
                  placeholder="Apellido Materno"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="titular_rut">RUT *</Label>
                <Input
                  id="titular_rut"
                  {...register('rut', { required: 'El RUT es requerido' })}
                  placeholder="12.345.678-9"
                />
                {errors.rut && (
                  <p className="text-sm text-red-500 mt-1">{errors.rut.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="titular_telefono">Teléfono</Label>
                <Input
                  id="titular_telefono"
                  {...register('telefono')}
                  placeholder="+56 9 1234 5678"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="titular_email">Email *</Label>
              <Input
                id="titular_email"
                type="email"
                {...register('mail', {
                  required: 'El email es requerido',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email inválido'
                  }
                })}
                placeholder="email@ejemplo.com"
              />
              {errors.mail && (
                <p className="text-sm text-red-500 mt-1">{errors.mail.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="titular_direccion">Dirección</Label>
              <Textarea
                id="titular_direccion"
                {...register('datos_titular.direccion')}
                placeholder="Dirección completa"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="titular_comuna">Comuna</Label>
                <Input
                  id="titular_comuna"
                  {...register('datos_titular.comuna')}
                  placeholder="Comuna"
                />
              </div>
              <div>
                <Label htmlFor="titular_region">Región</Label>
                <Input
                  id="titular_region"
                  {...register('datos_titular.region')}
                  placeholder="Región"
                />
              </div>
              <div>
                <Label htmlFor="titular_fecha_nacimiento">Fecha de Nacimiento</Label>
                <Input
                  id="titular_fecha_nacimiento"
                  type="date"
                  {...register('datos_titular.fecha_nacimiento')}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="estado">Estado</Label>
              <Select
                value={watchedData.estado}
                onValueChange={(value) => setValue('estado', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="pendiente_dps">Pendiente DPS</SelectItem>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="pendiente_pago">Pendiente Pago</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Cargas Familiares</h3>
              <Button type="button" onClick={addCarga} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Agregar Carga
              </Button>
            </div>
            
            {cargas.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No hay cargas familiares registradas</p>
                <p className="text-sm">Haz clic en "Agregar Carga" para añadir una nueva</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cargas.map((carga, index) => (
                  <Card key={carga.id || index} className="p-4">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-medium">Carga {index + 1}</h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeCarga(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <Label>Nombre</Label>
                        <Input
                          value={carga.nombre || ''}
                          onChange={(e) => updateCarga(index, 'nombre', e.target.value)}
                          placeholder="Nombre"
                        />
                      </div>
                      <div>
                        <Label>Apellido Paterno</Label>
                        <Input
                          value={carga.apellido_paterno || ''}
                          onChange={(e) => updateCarga(index, 'apellido_paterno', e.target.value)}
                          placeholder="Apellido Paterno"
                        />
                      </div>
                      <div>
                        <Label>Apellido Materno</Label>
                        <Input
                          value={carga.apellido_materno || ''}
                          onChange={(e) => updateCarga(index, 'apellido_materno', e.target.value)}
                          placeholder="Apellido Materno"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label>RUT</Label>
                        <Input
                          value={carga.rut || ''}
                          onChange={(e) => updateCarga(index, 'rut', e.target.value)}
                          placeholder="12.345.678-9"
                        />
                      </div>
                      <div>
                        <Label>Edad</Label>
                        <Input
                          type="number"
                          value={carga.edad || ''}
                          onChange={(e) => updateCarga(index, 'edad', e.target.value)}
                          placeholder="Edad"
                        />
                      </div>
                      <div>
                        <Label>Sexo</Label>
                        <Select 
                          value={carga.sexo || ''} 
                          onValueChange={(value) => updateCarga(index, 'sexo', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M">Masculino</SelectItem>
                            <SelectItem value="F">Femenino</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Previsión</Label>
                        <Input
                          value={carga.prevision || ''}
                          onChange={(e) => updateCarga(index, 'prevision', e.target.value)}
                          placeholder="Previsión"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <Label>Fecha de Nacimiento</Label>
                      <Input
                        type="date"
                        value={toDateInput(carga.fecha_nacimiento)}
                        onChange={(e) => updateCarga(index, 'fecha_nacimiento', e.target.value)}
                      />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium mb-2">Estado del Proceso</h3>
              <p className="text-muted-foreground">Resumen del estado actual de la contratación</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4 text-center">
                <div className="flex flex-col items-center space-y-2">
                  <div className="p-3 rounded-full" style={{ backgroundColor: lightTheme.warning.light }}>
                    <CreditCard className="h-6 w-6" style={{ color: lightTheme.warning.main }} />
                  </div>
                  <h4 className="font-medium">Orden de Pago</h4>
                  <Badge variant="outline" style={{ 
                    borderColor: lightTheme.warning.main,
                    color: lightTheme.warning.main 
                  }}>
                    Pendiente
                  </Badge>
                </div>
              </Card>
              
              <Card className="p-4 text-center">
                <div className="flex flex-col items-center space-y-2">
                  <div className="p-3 rounded-full" style={{ backgroundColor: lightTheme.info.light }}>
                    <Clock className="h-6 w-6" style={{ color: lightTheme.info.main }} />
                  </div>
                  <h4 className="font-medium">Pago</h4>
                  <Badge variant="outline" style={{ 
                    borderColor: lightTheme.info.main,
                    color: lightTheme.info.main 
                  }}>
                    Pendiente
                  </Badge>
                </div>
              </Card>
              
              <Card className="p-4 text-center">
                <div className="flex flex-col items-center space-y-2">
                  <div className="p-3 rounded-full" style={{ backgroundColor: lightTheme.error.light }}>
                    <AlertCircle className="h-6 w-6" style={{ color: lightTheme.error.main }} />
                  </div>
                  <h4 className="font-medium">Certificado</h4>
                  <Badge variant="outline" style={{ 
                    borderColor: lightTheme.error.main,
                    color: lightTheme.error.main 
                  }}>
                    No Emitido
                  </Badge>
                </div>
              </Card>
            </div>
            
            <Separator />
            
            <div className="space-y-4">
              <h4 className="font-medium">Resumen de Datos</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Nombre:</span> {watchedData.nombre || 'No especificado'}
                </div>
                <div>
                  <span className="font-medium">RUT:</span> {watchedData.rut || 'No especificado'}
                </div>
                <div>
                  <span className="font-medium">Email:</span> {watchedData.mail || 'No especificado'}
                </div>
                <div>
                  <span className="font-medium">Teléfono:</span> {watchedData.telefono || 'No especificado'}
                </div>
                <div>
                  <span className="font-medium">Estado:</span> {watchedData.estado || 'Pendiente'}
                </div>
                <div>
                  <span className="font-medium">Cargas:</span> {cargas.length} registradas
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  if (readOnly) {
    return <ContratanteViewer contratante={contratante} cargas={cargas} onClose={onClose} />;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>
            {contratante ? 'Editar Contratante' : 'Nuevo Contratante'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => {
              const status = getStepStatus(index);
              return (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(index)}
                      className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                        status === 'completed' 
                          ? 'border-green-500 bg-green-500 text-white'
                          : status === 'current'
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-gray-300 bg-white text-gray-500'
                      }`}
                    >
                      {getStepIcon(step, status)}
                    </button>
                    <div className="mt-2 text-center">
                      <p className={`text-sm font-medium ${
                        status === 'current' ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-400 max-w-24">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-16 h-0.5 mx-4 ${
                      index < currentStep ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {/* Step Content */}
          <Card>
            <CardHeader>
              <CardTitle>{steps[currentStep].title}</CardTitle>
            </CardHeader>
            <CardContent>
              {renderStepContent()}
            </CardContent>
          </Card>
          
          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t">
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  Anterior
                </Button>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                <X className="h-4 w-4 mr-2" />
                Cancelar
              </Button>
              
              {currentStep < steps.length - 1 ? (
                <Button
                  type="button"
                  onClick={() => setCurrentStep(currentStep + 1)}
                >
                  Siguiente
                </Button>
              ) : (
                <Button type="submit" disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Guardando...' : 'Guardar'}
                </Button>
              )}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContratanteForm;